import {
  ApolloClient,
  ApolloLink,
  NormalizedCacheObject,
  Operation,
} from '@apollo/client';
import { FetchResult } from '@apollo/client/link/core';
import { Observable, Observer } from '@apollo/client/utilities';

const MAX_QUEUE_SIZE = 100;
const QUEUE_TIMEOUT_MS = 30000;
const isDev = process.env.NODE_ENV === 'development';

interface QueuedOperation {
  operation: Operation;
  observer: Observer<FetchResult>;
  timestamp: number;
}

class OperationQueueManager {
  private queue: QueuedOperation[] = [];
  private readyClient: ApolloClient<NormalizedCacheObject> | null = null;
  private queueStartTime: number | null = null;
  private isQueueExpired = false;

  setReadyClient(client: ApolloClient<NormalizedCacheObject>) {
    if (this.readyClient && this.readyClient !== client) {
      this.clearQueue('Credentials changed');
    }

    this.readyClient = client;
    this.isQueueExpired = false;
    this.queueStartTime = null;
  }

  resetQueueState() {
    this.queue.length = 0;
    this.readyClient = null;
    this.isQueueExpired = false;
    this.queueStartTime = null;
  }

  hasQueuedOperations() {
    return this.queue.length > 0;
  }

  private clearQueue(reason: string) {
    if (isDev && this.queue.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`Clearing ${this.queue.length} queued operations: ${reason}`);
    }

    this.queue.forEach(({ observer, operation }) => {
      observer.error?.(new Error(`${reason}: ${operation.operationName}`));
    });

    this.queue.length = 0;
  }

  private checkQueueTimeout() {
    if (!this.queueStartTime) return;

    const now = Date.now();
    if (now - this.queueStartTime > QUEUE_TIMEOUT_MS) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(
          `Queue has been waiting for ${QUEUE_TIMEOUT_MS}ms without credentials. Marking as expired.`,
        );
      }
      this.isQueueExpired = true;
      this.clearQueue('Queue timeout - credentials never arrived');
    }
  }

  processQueue() {
    if (!this.readyClient || this.queue.length === 0) return;

    if (isDev) {
      // eslint-disable-next-line no-console
      console.log(`Processing ${this.queue.length} queued GraphQL operations`);
    }

    const queue = this.queue.splice(0);
    const now = Date.now();

    queue.forEach(({ operation, observer, timestamp }) => {
      if (now - timestamp > QUEUE_TIMEOUT_MS) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.warn(
            `Operation ${operation.operationName} timed out after ${QUEUE_TIMEOUT_MS}ms in queue`,
          );
        }
        observer.error?.(
          new Error(
            `Operation timed out waiting for credentials (${QUEUE_TIMEOUT_MS}ms)`,
          ),
        );
        return;
      }

      const observable = this.readyClient?.link.request(operation);

      if (observable) {
        observable.subscribe({
          next: result => observer.next?.(result),
          error: error => observer.error?.(error),
          complete: () => observer.complete?.(),
        });
      } else {
        observer.error?.(new Error('Failed to execute queued operation'));
      }
    });
  }

  createQueueLink() {
    return new ApolloLink(operation => {
      return new Observable(observer => {
        this.checkQueueTimeout();

        if (this.isQueueExpired) {
          const error = new Error(
            'Operation queue is expired. Credentials did not arrive within timeout period.',
          );
          if (isDev) {
            // eslint-disable-next-line no-console
            console.error('Queue is expired:', operation.operationName);
          }
          observer.error(error);
          return;
        }

        if (this.queue.length >= MAX_QUEUE_SIZE) {
          const error = new Error(
            `Operation queue is full (${MAX_QUEUE_SIZE} operations). Cannot queue more operations.`,
          );
          if (isDev) {
            // eslint-disable-next-line no-console
            console.error('Queue full:', operation.operationName);
          }
          observer.error(error);
          return;
        }

        // Initialize queue start time on first operation
        if (this.queue.length === 0) {
          this.queueStartTime = Date.now();
        }

        if (isDev) {
          // eslint-disable-next-line no-console
          console.log('Queueing operation:', operation.operationName);
        }

        this.queue.push({
          operation,
          observer,
          timestamp: Date.now(),
        });
      });
    });
  }
}

export const operationQueueManager = new OperationQueueManager();
